import React, { useMemo, useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { AlertCircle, CheckCircle2, AlertTriangle, Lightbulb } from 'lucide-react';

interface KeywordQualityValidatorProps {
  focusKeyword: string;
  articleTitle: string;
  currentArticleSlug?: string;
}

export const KeywordQualityValidator: React.FC<KeywordQualityValidatorProps> = ({ 
  focusKeyword,
  articleTitle
}) => {
  const [debouncedKeyword, setDebouncedKeyword] = useState(focusKeyword);
  const [debouncedTitle, setDebouncedTitle] = useState(articleTitle);

  // Debounce keyword and title changes
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedKeyword(focusKeyword);
      setDebouncedTitle(articleTitle);
    }, 500);

    return () => clearTimeout(timer);
  }, [focusKeyword, articleTitle]);

  const quality = useMemo(() => {
    const wordCount = debouncedKeyword.trim().split(/\s+/).length;
    
    if (!debouncedKeyword) {
      return { level: 'none', message: 'No focus keyword provided', icon: AlertCircle, color: 'text-gray-500', score: 0 };
    }
    
    // Check title alignment
    const titleLower = debouncedTitle.toLowerCase();
    const keywordLower = debouncedKeyword.toLowerCase();
    const titleWords = titleLower.split(/\s+/).filter(w => w.length > 3);
    const keywordWords = keywordLower.split(/\s+/).filter(w => w.length > 3);
    const matchingWords = keywordWords.filter(kw => 
      titleWords.some(tw => tw.includes(kw) || kw.includes(tw))
    );
    const titleMatchPercent = keywordWords.length > 0 ? (matchingWords.length / keywordWords.length) * 100 : 0;
    
    if (wordCount > 4) {
      return { 
        level: 'poor', 
        message: 'Focus keyword is too long. Keep it to 1-3 words for better SEO.',
        icon: AlertCircle,
        color: 'text-red-500',
        score: 20
      };
    }
    
    if (wordCount === 1 && debouncedKeyword.length < 4) {
      return { 
        level: 'poor', 
        message: 'Focus keyword is too short. Use keywords with at least 4 characters.',
        icon: AlertCircle,
        color: 'text-red-500',
        score: 25
      };
    }

    // Score based on title match
    if (titleMatchPercent < 30) {
      return { 
        level: 'poor', 
        message: 'Keyword doesn\'t match article title well. Use words from your title for better SEO.',
        icon: AlertCircle,
        color: 'text-red-500',
        score: 30
      };
    }

    if (titleMatchPercent < 50) {
      return { 
        level: 'moderate', 
        message: 'Partial title match. Consider including more title words in your keyword.',
        icon: AlertTriangle,
        color: 'text-yellow-500',
        score: 50
      };
    }

    if (titleMatchPercent < 70) {
      return { 
        level: 'good', 
        message: 'Good title alignment! Your keyword matches most of your title.',
        icon: CheckCircle2,
        color: 'text-blue-500',
        score: 75
      };
    }

    return { 
      level: 'excellent', 
      message: 'Perfect! Your focus keyword aligns perfectly with your article title.',
      icon: CheckCircle2,
      color: 'text-green-500',
      score: 100
    };
  }, [debouncedKeyword, debouncedTitle]);

  const Icon = quality.icon;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          <Lightbulb className="w-5 h-5" />
          Focus Keyword Quality
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Quality Assessment */}
        <div className="flex items-start gap-3 p-4 border rounded-lg bg-accent/50">
          <Icon className={`w-5 h-5 mt-0.5 ${quality.color}`} />
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1">
              <span className="font-semibold capitalize">{quality.level}</span>
            </div>
            <p className="text-sm text-muted-foreground">{quality.message}</p>
          </div>
        </div>

        {/* Quality Score */}
        {focusKeyword && (
          <div>
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium">Title Match Score</span>
              <span className="text-sm text-muted-foreground">{quality.score}/100</span>
            </div>
            <div className="w-full bg-secondary rounded-full h-2">
              <div 
                className={`h-2 rounded-full transition-all duration-500 ${
                  quality.score >= 70 ? 'bg-green-500' :
                  quality.score >= 50 ? 'bg-blue-500' :
                  quality.score >= 30 ? 'bg-yellow-500' :
                  'bg-red-500'
                }`}
                style={{ width: `${quality.score}%` }}
              />
            </div>
            <p className="text-xs text-muted-foreground mt-2">
              How well your focus keyword matches your article title
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
